import GameModel from "../../../vendor_core/core/src/model/GameModel";
import SpinModel from "../../../vendor_core/core/src/model/SpinModel";
import { State } from "../../../vendor_core/core/src/states/State";
import { DefaultLayers } from "../../../vendor_core/core/src/utils/Layers";
import ViewManager from "../../../vendor_core/core/src/utils/ViewManager";
import BURFeatureModel from "../models/BURFeatureModel";
import BURJackpotsView from "../view/BURJackpotsView";
import BURReelsModel from "../models/BURReelsModel";
import BURSpinModel from "../models/BURSpinModel";
import { PointLike } from "../types/BURGameTypes";
import BURReelSymbol from "../view/BURReelSymbol";
import gsap from "gsap";

export default class BURPlayCascadeState extends State {
    public async enter() {
        super.enter();

        const cascade =
            BURSpinModel.cascades[BURSpinModel.current_cascade_index];

        const isLast =
            cascade.cascadeIndex === BURSpinModel.cascades.length - 1;

        // Flip any mystery symbols from the previous cascade
        await this.flipCascadeMysterySymbols();

        // Clear the mystery symbols data
        BURSpinModel.setCascadeMysteryPositions([]);

        // Show the current cascade win
        BURReelsModel.reels.winlinesView.setWinBannerValue(cascade.creditsWon);

        // Play symbol win+explode animations
        await Promise.all(
            cascade.removedPositions.map(async (pos) => {
                const sbl = BURReelsModel.reels.getSymbolAt(
                    pos.x,
                    pos.y
                ) as BURReelSymbol;
                await sbl.playAsyncSpineAnim("win");
                await BURReelsModel.reels.destroyWinningSymbol(pos);
            })
        );

        // Clear the current cascade win
        const win = ViewManager.getLayer(DefaultLayers.PaylineCelebration);
        win.clear();

        // Drop the non-exploding symbols
        await Promise.all(
            BURReelsModel.reels.reels.map(async (reel, reelIndex) => {
                const newSymbolsOnReel = cascade.newSymbols[reelIndex];
                if (
                    newSymbolsOnReel.length > 0 &&
                    newSymbolsOnReel.length < reel.symbols.length
                ) {
                    const removedOnReel = new Set(
                        cascade.removedPositions
                            .filter((pos) => pos.x === reelIndex)
                            .map((pos) => pos.y)
                    );
                    await Promise.all(
                        reel.symbols.map(async (symbol, symbolIndex) => {
                            if (!removedOnReel.has(symbolIndex)) {
                                let positionsToDrop = 0;
                                removedOnReel.forEach((y) => {
                                    if (y > symbolIndex) {
                                        positionsToDrop++;
                                    }
                                });

                                if (positionsToDrop) {
                                    const finalY =
                                        reel.symbolsPositions[
                                            symbolIndex + positionsToDrop
                                        ].y;
                                    await this.dropNewSymbol(
                                        symbol as BURReelSymbol,
                                        finalY
                                    );
                                }
                            }
                        })
                    );
                }
            })
        );

        // Update the symbol ids
        const updatedView: number[][] = isLast
            ? SpinModel.reelFinalSymbols.get(BURReelsModel.reelset_index)!
            : BURSpinModel.cascades[BURSpinModel.current_cascade_index + 1]
                  .gridBefore;

        const cascadeMysterySymbols: PointLike[] = [];

        // Drop new symbols from above reels
        await Promise.all(
            cascade.newSymbols.map(async (reel, reelIndex) => {
                const currentReel = BURReelsModel.reels.reels[reelIndex];
                currentReel.sortSymbolsByY();
                const jpFragments = cascade.jackpotReveals?.filter(
                    (f) => f.x === reelIndex
                );
                await Promise.all(
                    reel.map(async (symbol, symbolIndex) => {
                        const sbl = currentReel.symbols[
                            symbolIndex
                        ] as BURReelSymbol;

                        const jpFragment = jpFragments?.find(
                            (f) => f.y === symbolIndex
                        );

                        // If the mystery symbol resolves into a jp fragment, don't set the symbol's real id yet
                        // It will be set in the jackpots progress state
                        if (jpFragment) {
                            sbl.setJackpotFragment(jpFragment.tier);
                            cascadeMysterySymbols.push({
                                x: reelIndex,
                                y: symbolIndex
                            });
                        } else {
                            const isMystery =
                                symbol === BURReelsModel.mystery_symbol_id ||
                                symbol === BURReelsModel.fs_mystery_symbol_id;
                            if (isMystery) {
                                cascadeMysterySymbols.push({
                                    x: reelIndex,
                                    y: symbolIndex
                                });
                            }
                            sbl.setIsMystery(isMystery);
                            sbl.setId(
                                isMystery
                                    ? updatedView[reelIndex][symbolIndex]
                                    : symbol
                            );
                        }
                        const destY =
                            currentReel.symbolsPositions[symbolIndex].y;
                        const delay = 0.3 - symbolIndex * 0.1;
                        await this.dropNewSymbol(sbl, destY, delay);
                    })
                );
            })
        );

        BURSpinModel.setCascadeMysteryPositions(cascadeMysterySymbols);

        // Set next cascade index
        BURSpinModel.setCurrentCascadeIndex(cascade.cascadeIndex + 1);

        if (isLast) {
            // Flip the mystery symbols
            await this.flipCascadeMysterySymbols();
            // Reset cascade mystery positions
            BURSpinModel.setCascadeMysteryPositions([]);
        }

        this.exit();
    }

    private async flipCascadeMysterySymbols() {
        await Promise.all(
            BURSpinModel.cascade_mystery_positions.map(async (pos) => {
                await BURReelsModel.reels.transformMysterySymbol(pos);
            })
        );

        // If any mystery symbols have transformed into energy coins, play the energy charge feature
        if (BURFeatureModel.mystery_energy_coins.length > 0) {
            await BURReelsModel.reels.playEnergyCharge(true);
            // Reset the energy coins from mystery flips
            BURFeatureModel.setMysteryEnergyCoins([]);
        }

        // Play the JP fragments' progress
        // First cascade is handled in the transform symbols state
        if (BURSpinModel.current_cascade_index > 0) {
            await this.playJackpotsProgress();
        }
    }

    private async playJackpotsProgress() {
        const prevCascade =
            BURSpinModel.cascades[BURSpinModel.current_cascade_index - 1];

        const jpFragments = prevCascade.jackpotReveals;

        if (!jpFragments || jpFragments.length === 0) return;

        // Destroy the jp fragments and show regular symbols
        const isLast =
            prevCascade.cascadeIndex === BURSpinModel.cascades.length - 1;
        const updatedView: number[][] = isLast
            ? SpinModel.reelFinalSymbols.get(BURReelsModel.reelset_index)!
            : BURSpinModel.cascades[BURSpinModel.current_cascade_index]
                  .gridBefore;

        jpFragments.forEach((fragment) => {
            fragment.symbolId = updatedView[fragment.x][fragment.y];
        });

        // Fill the jackpot tiers
        await BURJackpotsView.playJackpotsProgress(jpFragments);
    }

    private async dropNewSymbol(symbol: BURReelSymbol, y: number, delay = 0) {
        const anim = gsap
            .timeline()
            .to(symbol, {
                y: y * 1.02,
                duration: 0.15,
                delay,
                ease: "none"
            })
            .to(symbol, {
                y,
                duration: 0.15,
                ease: "none"
            });
        if (GameModel.turboSpin) {
            anim.timeScale(2.5);
        }
        await anim;
    }
}
